import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { Field, TextInput, TextArea } from "../forms/Fields";

const FIELDS = [
  ["subject", "Email subject", false],
  ["heading", "Heading", false],
  ["intro", "Opening line", false],
  ["message", "Your note", true],
  ["offer_heading", "Offer heading", false],
  ["offer_text", "Offer text", true],
  ["button_label", "Button text", false],
];

export const FollowupEmailPanel = () => {
  const [t, setT] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/admin/followup-email").then((r) => setT(r.data)).catch((e) => toast.error(errorMessage(e))); }, []);
  useEffect(() => {
    if (!t) return undefined;
    const id = setTimeout(() => api.post("/admin/followup-email/preview", t).then((r) => setPreview(r.data)).catch(() => {}), 400);
    return () => clearTimeout(id);
  }, [t]);

  const save = async (e) => {
    e.preventDefault();
    setBusy("save");
    try { await api.put("/admin/followup-email", t); toast.success("Follow-up email saved"); } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { const { data } = await api.post("/admin/followup-email/test"); toast.success(`Test sent to ${data.to}`); } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  if (!t) return <p className="font-sans text-slate">Loading...</p>;
  return (
    <div className="grid lg:grid-cols-[minmax(0,28rem)_1fr] gap-10 font-sans" data-testid="admin-followup-email">
      <form onSubmit={save} className="bg-paper rounded-2xl p-6 space-y-5 self-start" aria-label="Follow-up email wording" data-testid="followup-email-form">
        <p className="font-display text-2xl">Follow-up email</p>
        <p className="text-sm text-slate">Sent automatically, once, to everyone who downloads a resource. Type <code className="bg-sky px-1 rounded">{"{resource}"}</code> to insert the resource name.</p>
        {FIELDS.map(([k, label, long]) => (
          <Field key={k} id={`fu-${k}`} label={label} required>
            {long
              ? <TextArea id={`fu-${k}`} rows={4} required value={t[k]} onChange={(e) => setT({ ...t, [k]: e.target.value })} />
              : <TextInput id={`fu-${k}`} required value={t[k]} onChange={(e) => setT({ ...t, [k]: e.target.value })} />}
          </Field>
        ))}
        <div className="flex flex-wrap gap-3">
          <button type="submit" disabled={!!busy} className="inline-flex items-center gap-2 bg-teal text-paper rounded-full px-6 py-3 hover:bg-teal-deep disabled:opacity-70" data-testid="followup-save-button">
            {busy === "save" && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}Save wording
          </button>
          <button type="button" onClick={test} disabled={!!busy} className="inline-flex items-center gap-2 border border-ink/40 rounded-full px-5 py-3 hover:bg-sky disabled:opacity-70" data-testid="followup-test-button">
            {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Send className="w-4 h-4" aria-hidden="true" />}Send me a test
          </button>
        </div>
        <p className="text-xs text-slate">The test uses your saved wording, so save first.</p>
      </form>
      <div className="min-w-0">
        <p className="font-display text-2xl">Preview</p>
        <p className="mt-1 text-sm text-slate" data-testid="followup-preview-subject">Subject: {preview?.subject}</p>
        <div className="mt-4 bg-paper rounded-2xl overflow-hidden border border-ink/10">
          <iframe title="Follow-up email preview" srcDoc={preview?.html || ""} sandbox="" className="w-full h-[640px] border-0 bg-white" data-testid="followup-preview-frame" />
        </div>
      </div>
    </div>
  );
};
